import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Drawer, Summary, Timeline, Tabs, EmptyState } from './Overlay';
import { liveJobsCopy } from './liveJobsCopy';

/*
 * Arketipe daftar booking — Live jobs, Scheduled, Completed, Cancelled.
 *
 * Satu komponen karena di paket client memang satu fungsi (`bookingList` di
 * screens.js): kolom, aksi baris, drawer detail dan modal pembatalannya sama
 * persis di keempat layar. Yang berbeda cuma judul, kalimat pembuka, dan daftar
 * status yang diminta.
 *
 * Versi pertama Live jobs saya tulis sendiri dan meleset dari arketipe ini —
 * tanpa aksi baris, tanpa kolom Area/Status, dan teks empty state karangan.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $searchList: String, $statuses: String, $bookingType: Int, $categoryId: Int, $limit: Int) {
  getAllBookings(currentPage: $currentPage, searchList: $searchList, statuses: $statuses,
                 bookingType: $bookingType, categoryId: $categoryId, limit: $limit) {
    count status errorMessage
    results {
      id status totalFare currency createdAt completedAt updatedAt userLocation bookingType paymentStatus partnerId
      paymentType isPayoutPaid userPayableFare refundedAt refundAmount
      cancelReason { reason }
      userDetails { firstName lastName }
      partnerDetails { firstName lastName }
      categoryDetails { name }
      scheduleBooking { status scheduleFrom scheduleTo }
    }
  }
}`;

const Q_CATS = `{ getActiveCategories { results { id name } } }`;

const M_REFUND = `mutation ($bookingId: Int!, $amount: Float, $reason: String) {
  refundBooking(bookingId: $bookingId, amount: $amount, reason: $reason) {
    status errorMessage refundAmount
  }
}`;

const M_ASSIGN = `mutation ($bookingId: Int!, $partnerId: String!) {
  assignBooking(bookingId: $bookingId, partnerId: $partnerId) { status errorMessage }
}`;

const M_CANCEL = `mutation ($bookingId: Int!, $reason: String) {
  cancelBooking(bookingId: $bookingId, reason: $reason) { status errorMessage }
}`;

const Q_PROVIDERS = `{ getAssignableProviders { result { id name isActive } } }`;

// Label dan warna status, dari STATUS_META paket client. cancelledByAdmin
// ditambahkan — mutation pembatalan menuliskannya, dan prototipe tidak punya.
export const STATUS_META = {
  created: ['Requested', 'info'],
  approved: ['Accepted', 'amber'],
  arrived: ['On scene', 'warn'],
  started: ['In progress', 'amber'],
  paused: ['Paused', 'warn'],
  resumed: ['In progress', 'amber'],
  reviewed: ['Awaiting review', 'ok'],
  scheduled: ['Scheduled', 'info'],
  completed: ['Completed', 'ok'],
  cancelledByUser: ['Cancelled', 'danger'],
  cancelledByPartner: ['Declined', 'danger'],
  cancelledByAdmin: ['Cancelled', 'danger'],
  expired: ['Expired', 'danger'],
  refunded: ['Refunded', 'info']
};

// Daftar alasan dari prototipe. Tidak diambil dari tabel CancelReason: baris di
// situ ditujukan untuk pelanggan dan provider (userType 1/2), bukan admin.
const CANCEL_REASONS = ['Customer requested', 'No provider available', 'Duplicate booking',
  'Vehicle not accessible', 'Fraud suspected', 'Other'];

const FINAL = ['completed', 'expired', 'refunded', 'cancelledByUser',
  'cancelledByPartner', 'cancelledByAdmin'];

const isPaidDirect = r => r.bookingType === 3 && r.paymentStatus === 'completed';
const canAssign = r => !r.partnerId && r.status === 'created' && isPaidDirect(r);
const canCancel = r => FINAL.indexOf(r.status) < 0 && !isPaidDirect(r);

/*
 * Refund dulu hanya tersedia di halaman Direct Bookings, karena memang hanya
 * direct booking tanpa provider yang bisa direfund. Setelah syaratnya dilonggarkan
 * ke seluruh booking berbayar, membiarkannya di sana berarti sebagian besar
 * booking tetap tidak terjangkau — halaman itu hanya memuat yang belum ditugaskan.
 *
 * Syaratnya soal UANG, bukan tahapan pekerjaan: sudah dibayar lewat platform,
 * belum pernah dikembalikan, dan payout ke provider belum dilepas. Cash tidak
 * bisa direfund karena uangnya tidak pernah melewati platform.
 */
const canRefund = r =>
  r.paymentStatus === 'completed' &&
  r.paymentType !== 1 &&
  !r.refundedAt &&
  !r.isPayoutPaid;

const nameOf = p => (p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '');
const hhmm = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? '—' : d.toTimeString().slice(0, 5);
};
const dateTime = (v, locale) => {
  const d = new Date(Number(v) || v);
  if (isNaN(d)) return '—';
  return `${d.toLocaleDateString(locale === 'ar' ? 'ar-AE' : 'en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  })} · ${d.toLocaleTimeString(locale === 'ar' ? 'ar-AE' : 'en-GB', {
    hour: '2-digit', minute: '2-digit'
  })}`;
};
const rowTime = (r, current) => current === 'completed'
  ? (r.completedAt || r.createdAt)
  : current === 'cancelled' ? (r.updatedAt || r.createdAt) : r.createdAt;
const scheduleSlot = (schedule, locale, withDate = true) => {
  if (!schedule || !schedule.scheduleFrom) return '—';
  const from = new Date(Number(schedule.scheduleFrom) || schedule.scheduleFrom);
  const to = new Date(Number(schedule.scheduleTo) || schedule.scheduleTo);
  if (isNaN(from)) return '—';
  const lang = locale === 'ar' ? 'ar-AE' : 'en-GB';
  const day = from.toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d => isNaN(d) ? '' : d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
  return `${withDate ? `${day} · ` : ''}${time(from)}${isNaN(to) ? '' : `–${time(to)}`}`;
};
const since = (v, t) => {
  const ms = Date.now() - new Date(Number(v) || v).getTime();
  if (isNaN(ms)) return '';
  return t.age(Math.round(ms / 60000));
};

const StatusTag = ({ status, t }) => {
  const [, kind] = STATUS_META[status] || [status, ''];
  const label = t.status[status] || status;
  return <span className={`tag ${kind}`}><i />{label}</span>;
};

class BookingList extends React.Component {
  state = {
    rows: [], count: 0, page: 1, perPage: 10, search: '', categoryId: '',
    loading: true, error: null, cats: [], providers: [], exporting: false,
    viewing: null, tab: 0, assigning: null, chosen: '',
    cancelling: null, reason: '', saving: false, notice: null
  };

  componentDidMount() {
    this.load();
    this.loadCats();
    this.loadProviders();
    if (this.props.live) this.timer = setInterval(() => this.load(true), 30000);
  }
  componentWillUnmount() { clearInterval(this.timer); }

  copy = () => liveJobsCopy(['live', 'scheduled', 'completed', 'cancelled'].indexOf(this.props.current) >= 0
    ? this.props.intl.locale : 'en');

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    categoryId: this.state.categoryId ? Number(this.state.categoryId) : undefined,
    statuses: this.props.statuses,
    bookingType: this.props.bookingType,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAllBookings;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.copy().loadError });
      return;
    }
    this.setState({ rows: res.results || [], count: res.count || 0, loading: false });
  };

  loadCats = async () => {
    const json = await gql(Q_CATS).catch(() => null);
    const cats = json && json.data && json.data.getActiveCategories
      && json.data.getActiveCategories.results;
    if (cats && cats.length) this.setState({ cats });
  };

  loadProviders = async () => {
    const json = await gql(Q_PROVIDERS).catch(() => null);
    const res = json && json.data && json.data.getAssignableProviders;
    if (res && res.result) this.setState({ providers: res.result });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  setCategory = e => this.setState({ categoryId: e.target.value, page: 1 }, () => this.load());

  // Mengambil ulang dengan limit besar: yang tampil cuma satu halaman, dan CSV
  // berisi sepuluh baris diam-diam lebih buruk daripada tidak ada tombolnya.
  exportCsv = async () => {
    const t = this.copy();
    this.setState({ exporting: true });
    const json = await gql(Q, this.vars({ currentPage: 1, limit: 1000 })).catch(() => null);
    const res = json && json.data && json.data.getAllBookings;
    this.setState({ exporting: false });
    if (!res || res.status !== 200) return;

    const cell = v => '"' + String(v === null || v === undefined ? '' : v).replace(/"/g, '""') + '"';
    const scheduleHead = this.props.showSchedule ? [t.scheduledFor, t.scheduleState] : [];
    const rows = [
      [t.reference, t.customer, t.provider, t.service, t.area, ...scheduleHead,
        t.statusLabel, t.reason, t.fare, 'Currency',
        this.props.current === 'completed' ? t.completedAt
          : this.props.current === 'cancelled' ? t.closedAt : t.time],
      ...res.results.map(r => [
        `BK-${r.id}`, nameOf(r.userDetails), nameOf(r.partnerDetails) || t.unassigned,
        (r.categoryDetails || {}).name || '', r.userLocation || '',
        ...(this.props.showSchedule ? [
          scheduleSlot(r.scheduleBooking, this.props.intl.locale),
          (r.scheduleBooking && t.scheduleStatus[r.scheduleBooking.status]) || ''
        ] : []),
        t.status[r.status] || r.status, (r.cancelReason && r.cancelReason.reason) || '',
          Math.round(Number(r.totalFare) || 0), r.currency || 'AED',
          ['completed', 'cancelled'].indexOf(this.props.current) >= 0
            ? dateTime(rowTime(r, this.props.current), this.props.intl.locale)
            : hhmm(r.createdAt)
      ])
    ];
    const url = URL.createObjectURL(
      new Blob([rows.map(r => r.map(cell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `bas-${this.props.slug || 'bookings'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  assign = async () => {
    const t = this.copy();
    const { assigning, chosen } = this.state;
    if (!assigning || !chosen) return;
    this.setState({ saving: true });
    const json = await gql(M_ASSIGN, { bookingId: assigning.id, partnerId: chosen }).catch(() => null);
    const res = json && json.data && json.data.assignBooking;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || t.assignError } });
      return;
    }
    const who = (this.state.providers.find(p => p.id === chosen) || {}).name || t.providerFallback;
    this.setState({ assigning: null, chosen: '', notice: { tone: 'amber', text: t.assigned(assigning.id, who) } });
    this.load(true);
  };

  cancel = async () => {
    const t = this.copy();
    const { cancelling, reason } = this.state;
    if (!cancelling || !reason) return;
    this.setState({ saving: true });
    const json = await gql(M_CANCEL, { bookingId: cancelling.id, reason }).catch(() => null);
    const res = json && json.data && json.data.cancelBooking;
    this.setState({ saving: false });
    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || t.cancelError } });
      return;
    }
    this.setState({
      cancelling: null, reason: '',
      notice: { tone: 'danger', text: t.cancelledNotice(cancelling.id, t.reasons[reason] || reason) }
    });
    this.load(true);
  };

  /*
   * Nominal dan alasan ditanyakan, tidak diasumsikan.
   *
   * Refund kini menjangkau booking yang pekerjaannya sudah terjadi, dan di situ
   * jumlah yang benar tidak bisa diturunkan dari data — kalau pelanggan komplain
   * soal sebagian pekerjaan, hanya manusia yang tahu berapa yang pantas
   * dikembalikan. Kosongkan untuk refund penuh.
   */
  refund = async (booking) => {
    if (!booking) return;

    const paid = Number(booking.userPayableFare || booking.totalFare || 0);
    const rawAmount = window.prompt(
      `Refund booking BK-${booking.id}.\nPaid: ${booking.currency || ''} ${paid}\n\n` +
      `Leave empty for a FULL refund, or enter a smaller number for a partial refund.`
    );
    if (rawAmount === null) return;

    const amount = String(rawAmount).trim() === '' ? null : Number(rawAmount);
    if (amount !== null && (!Number.isFinite(amount) || amount <= 0)) {
      this.setState({ notice: { tone: 'danger', text: 'Enter a valid amount, or leave it empty for a full refund.' } });
      return;
    }

    const reason = window.prompt('Reason for this refund (required):');
    if (reason === null) return;
    if (String(reason).trim().length < 3) {
      this.setState({ notice: { tone: 'danger', text: 'A reason is required.' } });
      return;
    }

    this.setState({ saving: true });
    const json = await gql(M_REFUND, { bookingId: booking.id, amount, reason }).catch(() => null);
    const res = json && json.data && json.data.refundBooking;
    this.setState({ saving: false });

    if (!res || res.status !== 200) {
      this.setState({ notice: { tone: 'danger', text: (res && res.errorMessage) || 'Refund failed.' } });
      return;
    }

    this.setState({
      viewing: null,
      notice: {
        tone: 'success',
        text: `Refunded ${booking.currency || ''} ${res.refundAmount} for BK-${booking.id}.`
      }
    });
    this.load(true);
  };

  columns(t) {
    return [
      { key: 'id', label: t.reference, sortable: true, render: r => <span className="mono">BK-{r.id}</span> },
      {
        key: 'customer', label: t.customer, sortable: true,
        sortValue: r => nameOf(r.userDetails),
        render: r => nameOf(r.userDetails) || <span className="muted">{t.unknown}</span>
      },
      {
        key: 'provider', label: t.provider,
        render: r => nameOf(r.partnerDetails)
          ? nameOf(r.partnerDetails)
          : <span className="tag danger"><i />{t.unassigned}</span>
      },
      {
        key: 'service', label: t.service, sortable: true,
        sortValue: r => (r.categoryDetails || {}).name || '',
        render: r => (r.categoryDetails || {}).name || '—'
      },
      {
        // Prototipe menyebutnya Area. Booking tidak punya kolom area — yang
        // tersimpan alamat teks bebas, dan itu yang ditampilkan.
        key: 'userLocation', label: t.area,
        render: r => (
          <span title={r.userLocation || ''} style={{
            display: 'inline-block', maxWidth: 210, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom'
          }}>{r.userLocation || '—'}</span>
        )
      },
      ...(this.props.showSchedule ? [{
        key: 'scheduleBooking', label: t.scheduledFor, sortable: true,
        sortValue: r => Number(new Date(Number((r.scheduleBooking || {}).scheduleFrom)
          || (r.scheduleBooking || {}).scheduleFrom)) || 0,
        render: r => {
          const schedule = r.scheduleBooking;
          const status = schedule && schedule.status;
          const tone = status === 'completed' ? 'ok' : status === 'failed' ? 'danger' : 'info';
          return (
            <span>
              <span className="mono">{scheduleSlot(schedule, this.props.intl.locale)}</span>
              {status && <span className={`tag ${tone}`} style={{ display: 'flex', width: 'fit-content', marginTop: 4 }}>
                <i />{t.scheduleStatus[status] || status}
              </span>}
            </span>
          );
        }
      }] : []),
      { key: 'status', label: t.statusLabel, render: r => <StatusTag status={r.status} t={t} /> },
      ...(this.props.showReason ? [{
        // Hanya di layar Cancelled. Kolom kosong untuk booking yang dibatalkan
        // sebelum kolomnya ada, dan untuk yang kedaluwarsa sendiri — keduanya
        // memang tidak punya alasan yang tercatat.
        key: 'cancelReason', label: t.reason,
        render: r => (r.cancelReason && r.cancelReason.reason)
          ? <span title={r.cancelReason.reason}>{r.cancelReason.reason}</span>
          : <span className="muted">{r.status === 'expired' ? t.timedOut : t.notRecorded}</span>
      }] : []),
      {
        key: 'totalFare', label: t.fare, align: 'end', sortable: true,
        sortValue: r => Number(r.totalFare) || 0,
        render: r => `${r.currency || 'AED'} ${Math.round(Number(r.totalFare) || 0)}`
      },
      {
        key: ['completed', 'cancelled'].indexOf(this.props.current) >= 0 ? 'eventAt' : 'createdAt',
        label: this.props.current === 'completed' ? t.completedAt
          : this.props.current === 'cancelled' ? t.closedAt : t.time,
        align: 'end', sortable: true,
        sortValue: r => Number(rowTime(r, this.props.current)) || 0,
        render: r => (
          <span>
            <span className="mono">{['completed', 'cancelled'].indexOf(this.props.current) >= 0
              ? dateTime(rowTime(r, this.props.current), this.props.intl.locale)
              : hhmm(r.createdAt)}</span>
            {/* Umurnya ikut ditampilkan pada layar yang memang tentang apa yang
                sedang berjalan; pada daftar selesai itu cuma derau. */}
            {this.props.showAge && (
              <b style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--tx-3)' }}>
                {since(r.createdAt, t)}
              </b>
            )}
          </span>
        )
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title={t.viewBooking}
              aria-label={`${t.viewBooking} BK-${r.id}`}
              onClick={() => this.setState({ viewing: r, tab: 0 })}>
              <Icon name="eye" />
            </button>
            {canAssign(r) && (
              <button className="btn icon" type="button" title={t.assignProvider}
                aria-label={`${t.assignProvider} BK-${r.id}`}
                onClick={() => this.setState({ assigning: r, chosen: '', notice: null })}>
                <Icon name="route" />
              </button>
            )}
            {canCancel(r) && (
              <button className="btn icon" type="button" title={t.cancelBooking}
                aria-label={`${t.cancelBooking} BK-${r.id}`}
                onClick={() => this.setState({ cancelling: r, reason: '', notice: null })}>
                <Icon name="x" />
              </button>
            )}
          </span>
        )
      }
    ];
  }

  renderDrawer(t) {
    const { viewing, tab } = this.state;
    const at = hhmm(viewing.createdAt);
    const completedAt = dateTime(viewing.completedAt || viewing.createdAt, this.props.intl.locale);
    const closedAt = dateTime(viewing.updatedAt || viewing.createdAt, this.props.intl.locale);
    const fare = `${viewing.currency || 'AED'} ${Math.round(Number(viewing.totalFare) || 0)}`;
    const provider = nameOf(viewing.partnerDetails);
    const schedule = viewing.scheduleBooking;
    const slot = scheduleSlot(schedule, this.props.intl.locale);

    /*
     * Hanya peristiwa yang benar-benar bisa disimpulkan dari baris ini.
     * Prototipe menggambar seluruh siklus hidup dari data karangan; di sini
     * waktunya belum tersedia per-peristiwa sampai BookingHistory dibaca, jadi
     * yang ditampilkan tahapannya, bukan jamnya.
     */
    const events = [
      { title: t.bookingCreated, detail: `${nameOf(viewing.userDetails) || t.customerFallback} · ${(viewing.categoryDetails || {}).name || t.serviceFallback}`, at, color: 'var(--info)' }
    ];
    if (schedule) events.push({ title: t.scheduledFor, detail: slot, color: 'var(--info)' });
    if (viewing.bookingType === 3) events.push({ title: t.paidUpFront, detail: fare, color: 'var(--ok)' });
    if (provider) events.push({ title: t.providerAssigned, detail: provider, color: 'var(--brand-amber)' });
    if (['arrived', 'started', 'completed'].indexOf(viewing.status) >= 0) events.push({ title: t.arrivedScene, color: 'var(--warn)' });
    if (['started', 'completed'].indexOf(viewing.status) >= 0) events.push({ title: t.workStarted, color: 'var(--brand-amber-bright)' });
    if (viewing.status === 'completed') events.push({
      title: t.completedSettled, detail: fare, at: completedAt, color: 'var(--ok)'
    });
    if (String(viewing.status).indexOf('cancel') === 0) events.push({
      title: t.cancelled, at: closedAt, color: 'var(--danger)'
    });
    if (viewing.status === 'expired') events.push({ title: t.expiredEvent, at: closedAt, color: 'var(--danger)' });
    if (viewing.status === 'refunded') events.push({ title: t.refundedEvent, at: closedAt, color: 'var(--info)' });

    return (
      <Drawer
        title={`BK-${viewing.id}`}
        sub={[(viewing.categoryDetails || {}).name, viewing.userLocation,
          this.props.current === 'completed' ? completedAt
            : this.props.current === 'cancelled' ? closedAt : at].filter(Boolean).join(' · ')}
        headExtra={<StatusTag status={viewing.status} t={t} />}
        tabs={<Tabs labels={[t.overview, t.review, t.chat]} active={tab}
          onChange={t => this.setState({ tab: t })} />}
        onClose={() => this.setState({ viewing: null })}
        closeLabel={t.close}
        actions={[
          <button className="btn ghost" type="button" key="c"
            onClick={() => this.setState({ viewing: null })}>{t.close}</button>,
          canAssign(viewing) && (
            <button className="btn" type="button" key="a"
              onClick={() => this.setState({ assigning: viewing, viewing: null, chosen: '' })}>
              <Icon name="route" />{t.assignProvider}
            </button>
          ),
          provider && canCancel(viewing) && (
            <button className="btn ghost" type="button" key="x"
              onClick={() => this.setState({ cancelling: viewing, viewing: null, reason: '' })}>
              {t.cancelBooking}
            </button>
          ),
          canRefund(viewing) && (
            <button className="btn ghost" type="button" key="r"
              onClick={() => this.refund(viewing)}>
              Refund
            </button>
          )
        ]}
      >
        {tab === 0 && (
          <div>
            <Summary pairs={[
              [t.reference, <span className="mono">BK-{viewing.id}</span>],
              [t.statusLabel, <StatusTag status={viewing.status} t={t} />],
              [t.type, viewing.bookingType === 3 ? t.direct : viewing.bookingType === 2 ? t.scheduledType : t.standard],
              [t.customer, nameOf(viewing.userDetails) || t.unknown],
              [t.provider, provider || <span className="tag danger"><i />{t.unassigned}</span>],
              [t.service, (viewing.categoryDetails || {}).name || '—'],
              [t.area, viewing.userLocation || '—'],
              ...(schedule ? [
                [t.scheduledFor, slot],
                [t.scheduleState, t.scheduleStatus[schedule.status] || schedule.status]
              ] : []),
              ...(viewing.status === 'completed' ? [[t.completedAt, completedAt]] : []),
              ...(this.props.current === 'cancelled' ? [[t.closedAt, closedAt]] : []),
              [t.fare, fare]
            ]} />
            <div style={{
              fontSize: 'var(--fs-xs)', color: 'var(--tx-3)', letterSpacing: '.11em',
              textTransform: 'uppercase', fontWeight: 700, margin: '18px 0 12px'
            }}>{t.lifecycle}</div>
            <Timeline events={events} />
          </div>
        )}
        {tab === 1 && <EmptyState icon="star" title={t.noReview} msg={t.noReviewMsg} />}
        {tab === 2 && <EmptyState icon="chat" title={t.noMessages} msg={t.noMessagesMsg} />}
      </Drawer>
    );
  }

  render() {
    const {
      rows, count, page, perPage, loading, error, cats, categoryId, exporting,
      viewing, assigning, chosen, cancelling, reason, saving, notice, providers
    } = this.state;
    const { title, lead, current, breadcrumb, legacyHref } = this.props;
    const t = this.copy();
    const pageCopy = current === 'live' ? t.page
      : current === 'scheduled' ? t.scheduledPage
        : current === 'completed' ? t.completedPage
          : current === 'cancelled' ? t.cancelledPage
            : { title, lead, breadcrumb };

    return (
      <AdminShell
        current={current}
        breadcrumb={pageCopy.breadcrumb}
        title={pageCopy.title}
        lead={pageCopy.lead}
        actions={[
          <button className="btn ghost" type="button" key="x"
            onClick={this.exportCsv} disabled={exporting || !count}>
            <Icon name="dl" />{exporting ? t.preparing : t.exportCsv}
          </button>,
          legacyHref && <a className="btn ghost" href={legacyHref} key="c">{t.openClassic}</a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t.dismiss}</button>
          </div>
        )}

        {viewing && this.renderDrawer(t)}

        {assigning && (
          <Modal
            icon="route"
            title={t.assign(assigning.id)}
            sub={`${nameOf(assigning.userDetails) || t.customerFallback} · ${(assigning.categoryDetails || {}).name || t.serviceFallback}`}
            confirmLabel={t.confirmAssignment}
            cancelLabel={t.cancel}
            workingLabel={t.working}
            busy={saving}
            disabled={!chosen}
            onConfirm={this.assign}
            onClose={() => this.setState({ assigning: null, chosen: '' })}
          >
            <div className="field">
              <label>{t.provider}</label>
              <select className="inp" value={chosen}
                onChange={e => this.setState({ chosen: e.target.value })}>
                <option value="">{t.chooseProvider}</option>
                {providers.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name || t.unnamed}{p.isActive ? '' : ` · ${t.offline}`}
                  </option>
                ))}
              </select>
            </div>
          </Modal>
        )}

        {cancelling && (
          <Modal
            icon="x" tone="danger"
            title={t.cancelTitle(cancelling.id)}
            sub={t.cancelSub}
            confirmLabel={t.cancelBooking}
            cancelLabel={t.cancel}
            workingLabel={t.working}
            busy={saving}
            disabled={!reason}
            onConfirm={this.cancel}
            onClose={() => this.setState({ cancelling: null, reason: '' })}
          >
            <div className="field">
              <label>{t.cancellationReason}</label>
              {/* Wajib. Alasannya masuk ke BookingCancelReason, tabel yang sama
                  yang dipakai aplikasi untuk pembatalan oleh pelanggan dan
                  provider — sebelum ini alasan dari admin diminta lalu dibuang,
                  dan keluhan pelanggan tiga bulan kemudian tidak bisa dijawab. */}
              <select className="inp" value={reason}
                onChange={e => this.setState({ reason: e.target.value })}>
                <option value="">{t.chooseReason}</option>
                {CANCEL_REASONS.map(r => <option key={r} value={r}>{t.reasons[r]}</option>)}
              </select>
              {!reason && <span className="hint">{t.reasonRequired}</span>}
            </div>
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns(t)}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t.search}
              copy={t}
              filters={
                <select className="inp" value={categoryId} onChange={this.setCategory}
                  aria-label={t.filterService}>
                  <option value="">{t.allServices}</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              }
              emptyText={pageCopy.emptyTitle || this.props.emptyTitle}
              emptyHint={pageCopy.emptyMsg || this.props.emptyMsg}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(BookingList);
